import React from 'react';
import { useParams, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { tokens } from '../../../../public/tokens';
import Button from '../../../library/Button';
import VersionSummaryStripContainer from './VersionSummaryStripContainer';
import VersionDetailsForm from './versionDetails/VersionDetailsForm';

/*
 * Same loading/error/retry treatment `VersionCardsGrid.js` uses for the Versions list,
 * kept local here rather than shared since neither screen imports the other.
 */
const StyledMessage = styled.div({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.globals["spacing-2"],
    padding: `120px 0`,
});

const StyledMessageTitle = styled.p(({ $isError }) => ({
    margin: 0,
    color: $isError ? tokens.colors.states.alert["500"] : tokens.colors.text.primary,
    fontSize: tokens.typography.fontSize["xl-18px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
    fontWeight: tokens.typography.fontWeights.semibold,
}));

const StyledMessageBody = styled.p({
    margin: 0,
    color: tokens.colors.text.secondary,
    fontSize: tokens.typography.fontSize["lg-14px"],
    fontFamily: tokens.typography.fontFamilies["albert-sans"],
});

const StyledRetryButton = styled(Button)({
    height: "35px",
    borderRadius: tokens.globals["border-radius"],
    boxShadow: tokens.effects.effects.shadow.default,
});

/**
 * Version Details route wrapper (SPEC2): resolves the route params, shows
 * the version summary strip (skipped for "create", which has no version
 * yet), and renders the form. All field/save logic lives in
 * `VersionDetailsForm`/`useVersionDetailsForm`.
 * @param {boolean} [props.versionDataError] Set when the version's own data failed to load
 *   (distinct from `loading`, which also covers an in-flight Save) - shows a plain error with
 *   Retry instead of the form, per SPEC2 R1 point 5.
 * @param {() => void} [props.onRetryVersionData] Re-triggers the version data fetch.
 */
function VersionPageData(props) {
    const { versionData = {}, loading = null, setLoading, setRefresh, versionDataError = false, onRetryVersionData } = props;
    const { type } = useParams();
    const { search } = useLocation();
    const queryParams = new URLSearchParams(search);
    const versionId = queryParams.get("versionId");
    const programId = queryParams.get("programmeId");

    const isEditingExisting = type !== "create" && Boolean(versionId);
    /*
     * `loading` also covers an in-flight Save (see useVersionDetailsForm.js), which must keep
     * showing the form (with its sticky-bar spinner) rather than replace it with this skeleton -
     * so the initial-fetch skeleton only applies before the version's own data has arrived.
     */
    const isInitialLoad = isEditingExisting && Boolean(loading) && !versionData?.id;

    return (
        <div>
            {isEditingExisting && (
                <VersionSummaryStripContainer programmeId={programId} versionId={versionId} />
            )}

            {isEditingExisting && versionDataError ? (
                <StyledMessage>
                    <StyledMessageTitle $isError>This version couldn’t be loaded</StyledMessageTitle>
                    <StyledMessageBody>Something went wrong while loading this version. Retry to try again.</StyledMessageBody>
                    <StyledRetryButton type="button" variant="primary" size="lg" onClick={onRetryVersionData}>Retry</StyledRetryButton>
                </StyledMessage>
            ) : isInitialLoad ? (
                <StyledMessage>
                    <i className="fal fa-spinner fa-spin" aria-hidden="true" />
                    <StyledMessageBody>Loading version…</StyledMessageBody>
                </StyledMessage>
            ) : (
                <VersionDetailsForm
                    versionData={versionData}
                    type={type}
                    programId={programId}
                    versionId={versionId}
                    loading={loading}
                    setLoading={setLoading}
                    setRefresh={setRefresh}
                />
            )}
        </div>
    );
}

export default VersionPageData;
